import { createAsyncThunk } from "@reduxjs/toolkit";
import { showToast } from "../toast/reducer";
import {
    getAllFarmhouses,
    getFarmhouseDetails,
    createFarmhouse,
    updateFarmhouse,
    deleteFarmhouse,
    updateFarmhouseStatus as updateFarmhouseStatusAPI,
    uploadFarmhouseImages as uploadFarmhouseImagesAPI,
    addFarmhouseAmenities as addFarmhouseAmenitiesAPI,
    deleteFarmhouseAmenity as deleteFarmhouseAmenityAPI,
    deleteFarmhouseImage as deleteFarmhouseImageAPI,
    updateImagePriorities as updateImagePrioritiesAPI,
} from "../../helpers/apis";

export const fetchFarmhouses = createAsyncThunk(
    "farmhouse/fetchFarmhouses",
    async (params, thunkAPI) => {
        try {
            const response = await getAllFarmhouses(params);
            if (!response.error) {
                return response;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to fetch farmhouses";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const fetchFarmhouseDetails = createAsyncThunk(
    "farmhouse/fetchFarmhouseDetails",
    async (id, thunkAPI) => {
        try {
            const response = await getFarmhouseDetails(id);
            if (!response.error) {
                return response;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to fetch farmhouse details";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const addNewFarmhouse = createAsyncThunk(
    "farmhouse/addNewFarmhouse",
    async (data, thunkAPI) => {
        try {
            const response = await createFarmhouse(data);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Farmhouse created successfully" }));
                return response;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to create farmhouse";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const updateFarmhouseDetails = createAsyncThunk(
    "farmhouse/updateFarmhouseDetails",
    async ({ id, data }, thunkAPI) => {
        try {
            const response = await updateFarmhouse(id, data);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Farmhouse updated successfully" }));
                return response;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to update farmhouse";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const removeFarmhouse = createAsyncThunk(
    "farmhouse/removeFarmhouse",
    async (id, thunkAPI) => {
        try {
            const response = await deleteFarmhouse(id);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Farmhouse deleted successfully" }));
                return id;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to delete farmhouse";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const updateFarmhouseStatus = createAsyncThunk(
    "farmhouse/updateFarmhouseStatus",
    async ({ id, status }, thunkAPI) => {
        try {
            const response = await updateFarmhouseStatusAPI(id, status);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Status updated successfully" }));
                return { id, status };
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to update status";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

// Additional thunks for images and amenities can be added here
export const uploadFarmhouseImages = createAsyncThunk(
    "farmhouse/uploadFarmhouseImages",
    async ({ id, data }, thunkAPI) => {
        try {
            const response = await uploadFarmhouseImagesAPI(id, data);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Images uploaded successfully" }));
                return response;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to upload images";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const addFarmhouseAmenities = createAsyncThunk(
    "farmhouse/addFarmhouseAmenities",
    async ({ id, data }, thunkAPI) => {
        try {
            const response = await addFarmhouseAmenitiesAPI(id, data);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Amenities added successfully" }));
                return response;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to add amenities";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const deleteFarmhouseAmenity = createAsyncThunk(
    "farmhouse/deleteFarmhouseAmenity",
    async ({ farmId, amenityId }, thunkAPI) => {
        try {
            const response = await deleteFarmhouseAmenityAPI(farmId, amenityId);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Amenity deleted successfully" }));
                return { farmId, amenityId };
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to delete amenity";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const deleteFarmhouseImage = createAsyncThunk(
    "farmhouse/deleteFarmhouseImage",
    async ({ farmId, imageId }, thunkAPI) => {
        try {
            const response = await deleteFarmhouseImageAPI(farmId, imageId);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Image deleted successfully" }));
                return { farmId, imageId };
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to delete image";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const updateImagePriorities = createAsyncThunk(
    "farmhouse/updateImagePriorities",
    async ({ farmId, data }, thunkAPI) => {
        try {
            const response = await updateImagePrioritiesAPI(farmId, data);
            if (!response.error) {
                thunkAPI.dispatch(showToast({ type: "success", msg: "Image priorities updated successfully" }));
                return response;
            } else {
                thunkAPI.dispatch(showToast({ type: "error", msg: response.msg }));
                return thunkAPI.rejectWithValue(response.msg);
            }
        } catch (error) {
            const errorMsg = error.message || "Failed to update image priorities";
            thunkAPI.dispatch(showToast({ type: "error", msg: errorMsg }));
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

